import { useState } from "react";
import { Download, Loader2, Check } from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { tokens } from "@/design-system/tokens";

function getPlatformInstructions() {
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) {
    if (/CriOS/.test(ua)) return "Tap and hold the download link → Open in Files. Then upload from Files on desktop.";
    return "Tap the file → tap the share icon → Save to Files. Then upload from Files on desktop.";
  }
  if (/Android/.test(ua)) return "File will save to your Downloads folder automatically.";
  return null; // Desktop — no extra instructions
}

// Same anatomy as Settings' IntegrationRow so every block on the
// Integrations tab reads identically: icon chip left with the ? help toggle
// under it, title + action button level on the icon line, any longer text in
// a hairline band below (bands, not nested boxes — design-system rule).
export const ExportCard = ({ title, icon, helpText, endpoint, filename, testId }) => {
  const [loading, setLoading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const handleExport = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(endpoint, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fretless-${filename}-${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloaded(true);
      setShowInstructions(true);
      toast.success(`${title} downloaded!`);
      setTimeout(() => setDownloaded(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const instructions = getPlatformInstructions();

  return (
    <div
      style={{
        padding: tokens.space.lg,
        border: tokens.border.card,
        borderRadius: tokens.radius.lg,
        backgroundColor: tokens.color.cardBg,
      }}
      data-testid={testId}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: tokens.space.md }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: tokens.space.sm, flexShrink: 0 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: tokens.radius.md,
              background: tokens.color.innerBg,
              border: tokens.border.inner,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </div>
          {helpText && (
            <button
              type="button"
              onClick={() => setHelpOpen(o => !o)}
              aria-label="More info"
              aria-expanded={helpOpen}
              data-testid={testId ? `${testId}-help` : undefined}
              style={{
                width: 18,
                height: 18,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                color: helpOpen ? tokens.color.orange : tokens.color.textMuted,
                border: tokens.border.inner,
                borderRadius: tokens.radius.pill,
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 700,
                fontFamily: tokens.font.body,
                lineHeight: 1,
                flexShrink: 0,
                transition: `color ${tokens.motion.fast}`,
              }}
              onMouseEnter={(e) => { if (!helpOpen) e.currentTarget.style.color = tokens.color.text; }}
              onMouseLeave={(e) => { if (!helpOpen) e.currentTarget.style.color = tokens.color.textMuted; }}
            >
              ?
            </button>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: tokens.space.md, minHeight: 36 }}>
            <h4 style={{ margin: 0, ...tokens.type.titleSm, color: tokens.color.text }}>{title}</h4>
            <button
              onClick={handleExport}
              disabled={loading}
              style={{
                backgroundColor: "transparent",
                color: tokens.color.orange,
                border: tokens.border.card,
                borderRadius: tokens.radius.md,
                padding: `${tokens.space.xs}px ${tokens.space.md}px`,
                fontWeight: 600,
                fontSize: "12px",
                cursor: loading ? "not-allowed" : "pointer",
                fontFamily: tokens.font.body,
                display: "flex",
                alignItems: "center",
                gap: `${tokens.space.xs}px`,
                opacity: loading ? 0.7 : 1,
                marginLeft: "auto",
                flexShrink: 0,
              }}
              data-testid={`${testId}-btn`}
            >
              {loading ? (
                <Loader2 style={{ height: "16px", width: "16px", animation: "spin 1s linear infinite" }} />
              ) : downloaded ? (
                <Check style={{ height: "16px", width: "16px", color: tokens.color.green }} />
              ) : (
                <Download style={{ height: "16px", width: "16px" }} />
              )}
              {downloaded ? "Downloaded" : "Export CSV"}
            </button>
          </div>
        </div>
      </div>
      {helpOpen && helpText && (
        <div
          style={{
            marginTop: tokens.space.md,
            paddingTop: tokens.space.md,
            borderTop: tokens.border.hairline,
            ...tokens.type.bodySm,
            color: tokens.color.textDim,
          }}
        >
          {helpText}
        </div>
      )}
      {showInstructions && instructions && (
        <div
          style={{
            marginTop: tokens.space.md,
            paddingTop: tokens.space.md,
            borderTop: tokens.border.hairline,
            ...tokens.type.bodySm,
            color: tokens.color.amber,
          }}
        >
          {instructions}
        </div>
      )}
    </div>
  );
};
